import { CATEGORIES, type Category, type Song } from '../data/mockCatalog';
import { DIFFICULTIES, FORMAT_LABELS, KO_DRUM_NAMES, EN_DRUM_NAMES } from '../data/constants';

export function getCategoryLabel(categoryId: string, language: string, categories: Category[] = CATEGORIES) {
  const category = categories.find((item) => item.categoryId === categoryId);
  return language === 'ko' ? category?.nameKo : category?.nameEn;
}

export function getDifficultyData(song: Song | null | undefined, difficulty: 'Easy' | 'Normal' | 'Hard') {
  return song?.difficulties?.[difficulty] || null;
}

export function getFormatLabel(song: Song | null | undefined, difficulty: 'Easy' | 'Normal' | 'Hard') {
  return FORMAT_LABELS[getDifficultyData(song, difficulty)?.format || ''] || DIFFICULTIES.find((item) => item.id === difficulty)?.format || '';
}

export function canPlayDifficulty(song: Song | null | undefined, difficulty: 'Easy' | 'Normal' | 'Hard', hasDrumPass: boolean) {
  return Boolean(hasDrumPass || getDifficultyData(song, difficulty)?.freeAccess);
}

export interface SongAccess {
  type: 'pass' | 'free' | 'locked' | 'partial';
  label: string;
  shortLabel: string;
}

export function getSongAccess(song: Song, hasDrumPass: boolean, language: string): SongAccess {
  if (hasDrumPass) return { type: 'pass', label: language === 'ko' ? '완전 해제' : 'FULLY UNLOCKED', shortLabel: language === 'ko' ? '해제' : 'OPEN' };
  const freeCount = DIFFICULTIES.filter((item) => getDifficultyData(song, item.id)?.freeAccess).length;
  if (freeCount === DIFFICULTIES.length) return { type: 'free', label: 'FREE', shortLabel: 'FREE' };
  if (freeCount === 0) return { type: 'locked', label: language === 'ko' ? '잠김' : 'LOCKED', shortLabel: language === 'ko' ? '잠김' : 'LOCKED' };
  return { type: 'partial', label: language === 'ko' ? `부분 무료 ${freeCount}/3` : `PARTLY FREE ${freeCount}/3`, shortLabel: `FREE ${freeCount}/3` };
}

export function resetInfiniteRail(container: HTMLElement | null) {
  if (!container) return;
  const middleStart = container.querySelector<HTMLElement>('[data-loop-copy="1"]');
  if (!middleStart) {
    container.scrollLeft = 0;
    return;
  }
  const padding = Number.parseFloat(getComputedStyle(container).paddingLeft) || 0;
  container.scrollLeft = middleStart.offsetLeft - padding;
}

export function normalizeInfiniteRail(container: HTMLElement | null) {
  if (!container) return;
  const middleStart = container.querySelector<HTMLElement>('[data-loop-copy="1"]');
  const nextStart = container.querySelector<HTMLElement>('[data-loop-copy="2"]');
  if (!middleStart || !nextStart) return;
  const padding = Number.parseFloat(getComputedStyle(container).paddingLeft) || 0;
  const middleOffset = middleStart.offsetLeft - padding;
  const nextOffset = nextStart.offsetLeft - padding;
  const loopWidth = nextOffset - middleOffset;
  if (loopWidth <= 0) return;
  const lowerBoundary = middleOffset - (loopWidth / 2);
  const upperBoundary = middleOffset + (loopWidth / 2);
  if (container.scrollLeft < lowerBoundary) container.scrollLeft += loopWidth;
  else if (container.scrollLeft >= upperBoundary) container.scrollLeft -= loopWidth;
}

export function centerInfiniteRailItem(container: HTMLElement | null, target: HTMLElement | null) {
  if (!container || !target) return;
  container.scrollLeft = target.offsetLeft + (target.offsetWidth / 2) - (container.clientWidth / 2);
  normalizeInfiniteRail(container);
}

export function scrollScoreTargetToTop(target: Element | null | undefined) {
  const viewport = target?.closest('.score-viewport');
  if (!target || !viewport) return;
  const targetRect = target.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  const top = viewport.scrollTop + targetRect.top - viewportRect.top - 8;
  viewport.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function getDrumDisplayName(name: string, language: string) {
  return language === 'ko' ? (KO_DRUM_NAMES[name] || name) : (EN_DRUM_NAMES[name] || name);
}

export function getMetricStatus(score: number, language: string) {
  if (score >= 90) return language === 'ko' ? '매우 좋음' : 'Excellent';
  if (score >= 80) return language === 'ko' ? '좋음' : 'Good';
  if (score >= 70) return language === 'ko' ? '안정' : 'Steady';
  return language === 'ko' ? '연습 중' : 'Practicing';
}

export function formatProfileDate(isoDate: string | undefined, language: string) {
  if (!isoDate) return '';
  const date = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  if (language === 'ko') {
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function profileInitial(name: string | undefined) {
  const trimmed = (name || '').trim();
  return trimmed ? trimmed.slice(0, 1).toUpperCase() : '?';
}
